import { StudentInterface } from "../Interfaces/Entities"

// Tags are stored inside the text as @[name](id), the same format the mobile app writes.
// While typing we only show @name and keep the tagged students next to the text.
export interface TaggedText {
	text: string
	students: StudentInterface[]
}

export interface TaggedTextPart {
	text: string
	student?: StudentInterface
}

export interface TagQuery {
	start: number
	query: string
}

export const EMPTY_TAGGED_TEXT: TaggedText = { text: '', students: [] }

const STORED_TAG_PATTERN = '@\\[([^\\]]+)\\]\\(([^)]+)\\)'
const MAX_TAG_QUERY_LENGTH = 40
const MAX_TAG_SUGGESTIONS = 8

export function GetStudentTagName(student: StudentInterface): string {
	return student.name?.trim() || student.rut
}

export function NormalizeForSearch(text: string): string {
	return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function ParseTaggedText(storedText: string | undefined): TaggedTextPart[] {
	if (!storedText) {
		return []
	}
	const regex = new RegExp(STORED_TAG_PATTERN, 'g')
	const parts: TaggedTextPart[] = []
	let lastIndex = 0
	let match = regex.exec(storedText)
	while (match) {
		if (match.index > lastIndex) {
			parts.push({ text: storedText.slice(lastIndex, match.index) })
		}
		parts.push({ text: `@${match[1]}`, student: { name: match[1], rut: match[2] } })
		lastIndex = match.index + match[0].length
		match = regex.exec(storedText)
	}
	if (lastIndex < storedText.length) {
		parts.push({ text: storedText.slice(lastIndex) })
	}
	return parts
}

export function StripTags(storedText: string | undefined): string {
	return ParseTaggedText(storedText).map((part) => part.text).join('')
}

export function GetTaggedStudentIds(storedText: string): string[] {
	const ids = ParseTaggedText(storedText)
		.filter((part) => part.student)
		.map((part) => (part.student as StudentInterface).rut)
	return Array.from(new Set(ids))
}

// The @ must start a word, so emails like ana@colegio.cl don't open the list.
export function FindTagQuery(text: string, cursor: number): TagQuery | undefined {
	const start = text.lastIndexOf('@', cursor - 1)
	if (start < 0 || start >= cursor) {
		return undefined
	}
	if (start > 0 && !/\s/.test(text[start - 1])) {
		return undefined
	}
	const query = text.slice(start + 1, cursor)
	if (query.length > MAX_TAG_QUERY_LENGTH || query.includes('\n') || query.startsWith(' ')) {
		return undefined
	}
	return { start, query }
}

export function FilterStudentsForTag(students: StudentInterface[], query: string): StudentInterface[] {
	const normalizedQuery = NormalizeForSearch(query.trim())
	return students
		.filter((student) => NormalizeForSearch(GetStudentTagName(student)).includes(normalizedQuery))
		.slice(0, MAX_TAG_SUGGESTIONS)
}

export function InsertTag(value: TaggedText, tagQuery: TagQuery, cursor: number,
	student: StudentInterface): { value: TaggedText, cursor: number } {
	const before = value.text.slice(0, tagQuery.start)
	const after = value.text.slice(cursor).replace(/^ /, '')
	const inserted = `@${GetStudentTagName(student)} `
	const isAlreadyTagged = value.students.some((taggedStudent) => taggedStudent.rut === student.rut)
	return {
		value: {
			text: before + inserted + after,
			students: isAlreadyTagged ? value.students : [...value.students, student],
		},
		cursor: before.length + inserted.length,
	}
}

// A tag whose name was edited no longer matches, so it is saved as plain text.
export function EncodeTaggedText({ text, students }: TaggedText): string {
	if (students.length === 0) {
		return text
	}
	const studentsByName = new Map<string, StudentInterface>()
	students.forEach((student) => studentsByName.set(GetStudentTagName(student), student))
	const names = Array.from(studentsByName.keys()).sort((a, b) => b.length - a.length)
	const pattern = new RegExp(`@(${names.map(EscapeRegExp).join('|')})(?![\\p{L}\\p{N}])`, 'gu')
	return text.replace(pattern, (_, name: string) => {
		const student = studentsByName.get(name) as StudentInterface
		return `@[${name}](${student.rut})`
	})
}

function EscapeRegExp(text: string): string {
	return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
